/**
 * ⚠️ LR DIRECT FORK — do not merge into marketplace hooks.
 *
 * Per-shipper credit rating for the broker-facing credit box.
 * Calls a SECURITY DEFINER RPC that returns ONLY the rating values stored on
 * the load's shipper company — no name, no company, no identifying field — so
 * blind (open-market) bidding stays blind.
 *
 * Values are admin-set on the company record (Admin → Companies).
 * When a shipper has no values stored, the previous hardwired reference
 * numbers are shown, so display behavior is unchanged for unrated shippers.
 */

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface ShipperCreditRating {
  grade: string;
  score: number;
  label: string;
  daysToPay: string;
  paymentTrend: string;
  recommendedExposure: string;
  reportedPayments: string;
}

/** Legacy hardwired values — used when the shipper has no stored rating. */
export const FALLBACK_SHIPPER_CREDIT: ShipperCreditRating = {
  grade: "A-",
  score: 88,
  label: "Strong",
  daysToPay: "26 days",
  paymentTrend: "Stable",
  recommendedExposure: "$75k",
  reportedPayments: "42",
};

function labelForScore(score: number): string {
  if (score >= 85) return "Strong";
  if (score >= 70) return "Fair";
  return "Caution";
}

export function useDirectShipperCredit(
  loadId: string | null | undefined,
): { rating: ShipperCreditRating; isCustom: boolean; loading: boolean } {
  const { data, isLoading } = useQuery({
    queryKey: ["direct-shipper-credit", loadId],
    enabled: !!loadId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_direct_shipper_credit", {
        _load_id: loadId,
      });
      if (error) throw error;
      const row: any = Array.isArray(data) ? data[0] : data;
      return row ?? null;
    },
  });

  if (!data) {
    return { rating: FALLBACK_SHIPPER_CREDIT, isCustom: false, loading: isLoading };
  }

  const score =
    data.credit_score != null && !isNaN(Number(data.credit_score))
      ? Number(data.credit_score)
      : null;

  /* A shipper counts as rated once any rating value is stored. Each individual
     field still falls back on its own so a partially filled record renders. */
  const isCustom =
    score != null ||
    !!data.credit_grade ||
    data.days_to_pay != null ||
    !!data.payment_trend ||
    !!data.recommended_exposure ||
    !!data.reported_payments;

  if (!isCustom) {
    return { rating: FALLBACK_SHIPPER_CREDIT, isCustom: false, loading: isLoading };
  }

  const resolvedScore = score ?? FALLBACK_SHIPPER_CREDIT.score;

  return {
    rating: {
      grade: data.credit_grade || FALLBACK_SHIPPER_CREDIT.grade,
      score: resolvedScore,
      label: data.credit_rating_label || labelForScore(resolvedScore),
      daysToPay:
        data.days_to_pay != null
          ? `${Number(data.days_to_pay)} days`
          : FALLBACK_SHIPPER_CREDIT.daysToPay,
      paymentTrend: data.payment_trend || FALLBACK_SHIPPER_CREDIT.paymentTrend,
      recommendedExposure:
        data.recommended_exposure || FALLBACK_SHIPPER_CREDIT.recommendedExposure,
      reportedPayments:
        data.reported_payments || FALLBACK_SHIPPER_CREDIT.reportedPayments,
    },
    isCustom: true,
    loading: isLoading,
  };
}
